// Offer > Compensation (lane B8 S13): the offer package with its six BRD chapter 6 rows, the Recruiter's prepared
// fields, the conditional approvals (applicable rows only, leader and Total Rewards first), the approver controls on
// the one actionable row, the leader's evidence, the Total Rewards amount and wording, and the Offer readiness gate
// that holds Extend verbal offer until every applicable approval is complete. The server projects the restricted
// rows away from the Hiring Manager; this view draws what arrives and never guesses a hidden value.
import { useState } from "react";
import { Card, ErrorStrip, Skeleton, Split, Stack } from "@/mc";
import { uploadFile } from "@/api/runtime";
import { compApprove, compApproveTotalRewards, compOpenWorksheet, compPropose, compRecordLeaderApproval, compRecordPackage } from "@/api/tools";
import { errorText, isClientRefusal, useRead, useWrite } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import type { OfferTabProps } from "../types";
import { ApprovalsPanel } from "./ApprovalsPanel";
import { PackageCard } from "./PackageCard";
import { PrepareForm } from "./PrepareForm";
import { ReadinessGate } from "./ReadinessGate";
import { PKG_LABELS } from "./labels";

export type ApprovalKey = "hm" | "hrbp" | "org_leader" | "total_rewards";
export type ApprovalState = "pending" | "approved" | "rejected" | "revision_required";
export type PackageStatus = "draft" | "pending_approvals" | "ready_for_verbal" | "rejected" | "revision_required";
export type Decision = "approved" | "rejected" | "revision_required";
export type Amount = number | string | null | undefined;

export interface CompRange { min?: Amount; max?: Amount; currency?: string | null }

export interface CompApproval {
  requirement: ApprovalKey;
  state: ApprovalState;
  person?: string | null;
  approver_name?: string | null;
  version?: number | null;
  note?: string | null;
  evidence?: { file_id?: string | null; ref?: string | null } | null;
  approved_amount?: Amount;
  approved_wording?: string | null;
  at?: string | null;
  /** The viewer's own act (the Hiring Manager sees row 6 only for these). */
  own?: boolean;
}

export interface CompPackage {
  version: number;
  status: PackageStatus;
  base?: Amount;
  bonus_target?: Amount;
  commission?: Amount;
  sign_on?: Amount;
  relocation?: Amount;
  lti?: Amount;
  lti_wording?: string | null;
  currency?: string | null;
  start_date?: string | null;
  final_terms?: string | null;
  requirements?: ApprovalKey[];
}

export interface CompReadiness {
  ready: boolean;
  complete?: ApprovalKey[];
  missing?: ApprovalKey[];
}

/** comp.open_worksheet's V2 shape (PARALLEL-MAP §4). Restricted keys are absent for a seat below them. */
export interface CompOut {
  application_id: string;
  requisition_id: string;
  level?: string;
  comp_status?: string | null;
  recommendation?: { text?: string | null; range?: CompRange | null } | null;
  salary_grade?: string | null;
  market?: { data?: string | null; internal_range?: string | null; positioning?: string | null } | null;
  working_notes?: string | null;
  internal_comparison?: Record<string, unknown> | null;
  package?: CompPackage | null;
  history?: { version?: number }[];
  approvals?: CompApproval[];
  readiness?: CompReadiness | null;
  can: { prepare?: boolean; send?: boolean; approve?: boolean; record_leader?: boolean; approve_total_rewards?: boolean };
}

export interface PrepareValues {
  base: string;
  bonus_target: string;
  commission: string;
  sign_on: string;
  relocation: string;
  lti: string;
  lti_wording: string;
  start_date: string;
}

export interface DecideInput {
  key: ApprovalKey;
  decision: Decision;
  note?: string;
  evidence?: { file_id?: string; ref?: string };
  /** The organizational leader who approved (recorded by the HM). */
  approver_name?: string;
  approved_amount?: string;
  approved_wording?: string;
}

export interface CompActions {
  prepare?: (values: PrepareValues) => Promise<unknown>;
  send?: () => Promise<unknown>;
  decide?: (input: DecideInput) => Promise<unknown>;
  /** Stores the leader's evidence file and returns its id. */
  upload?: (file: File) => Promise<string>;
  extend?: () => void;
}

/** The leader and Total Rewards rows come first, then the Hiring Manager and the HR Business Partner. */
export const APPROVAL_ORDER: readonly ApprovalKey[] = ["org_leader", "total_rewards", "hm", "hrbp"];

/** The Hiring Manager's limited projection: the server leaves row 4 (salary grade) out. */
export const isLimited = (comp: CompOut): boolean => !("salary_grade" in comp);

/** The applicable approvals of the current package, in panel order. */
export function applicable(comp: CompOut): CompApproval[] {
  const pkg = comp.package;
  if (!pkg) return [];
  // approvals[] holds every sent version's rows (carried ones included); this package's are the current version's,
  // and a version not yet sent has none, so each of its requirements reads Pending
  const current = (comp.approvals ?? []).filter((row) => row.version == null || row.version === pkg.version);
  const required = pkg.requirements ?? current.map((row) => row.requirement);
  const rows = required.map((key) => current.find((row) => row.requirement === key) ?? { requirement: key, state: "pending" as const });
  return [...rows].sort((a, b) => APPROVAL_ORDER.indexOf(a.requirement) - APPROVAL_ORDER.indexOf(b.requirement));
}

/** The one row this viewer may act on now, or null. */
export function actionableRow(comp: CompOut, roles: readonly string[]): ApprovalKey | null {
  const pkg = comp.package;
  if (!pkg || pkg.status !== "pending_approvals") return null;
  for (const row of applicable(comp)) {
    if (row.state !== "pending") continue;
    if (row.requirement === "org_leader" && comp.can.record_leader) return row.requirement;
    if (row.requirement === "total_rewards" && comp.can.approve_total_rewards) return row.requirement;
    if ((row.requirement === "hm" || row.requirement === "hrbp") && comp.can.approve && roles.includes(row.requirement)) return row.requirement;
  }
  return null;
}

const VIEW_ONLY_ROLES = ["ta_manager", "coordinator"];
const ACTING_ROLES = ["recruiter", "hm", "hrbp", "compensation", "admin"];

/** TA Manager and Coordinator read the tab (mode "view"); they hold no control on it. */
export const viewOnly = (roles: readonly string[]): boolean =>
  roles.some((role) => VIEW_ONLY_ROLES.includes(role)) && !roles.some((role) => ACTING_ROLES.includes(role));

export interface CompensationViewProps {
  comp: CompOut;
  roles: readonly string[];
  actions?: CompActions | undefined;
  timeZone?: string | undefined;
  /** A write's refusal or failure, shown above the cards until the next write succeeds. */
  error?: string | undefined;
}

/** The pure view (the SSR test renders it per seat without a network). */
export function CompensationView({ comp, roles, actions = {}, timeZone, error }: CompensationViewProps) {
  const view = viewOnly(roles);
  const limited = isLimited(comp);
  const pkg = comp.package ?? null;
  const editable = !pkg || pkg.status === "draft" || pkg.status === "revision_required";
  const showPrepare = !view && roles.includes("recruiter") && comp.can.prepare === true && editable;
  return (
    <Stack>
      {error ? <ErrorStrip message={error} /> : null}
      <Split
        main={
          <Stack>
            <PackageCard comp={comp} limited={limited} />
            {showPrepare ? (
              <PrepareForm comp={comp} canSend={comp.can.send === true && pkg !== null} onSave={actions.prepare} onSend={actions.send} />
            ) : null}
            <ApprovalsPanel
              comp={comp}
              limited={limited}
              actionable={view ? null : actionableRow(comp, roles)}
              onDecide={actions.decide}
              onUpload={actions.upload}
              timeZone={timeZone}
            />
          </Stack>
        }
        side={<ReadinessGate comp={comp} mode={view || !roles.includes("recruiter") ? "view" : undefined} onExtend={actions.extend} />}
      />
    </Stack>
  );
}

const blank = (value: string): string | null => (value.trim() === "" ? null : value.trim());

/** The tab: reads the worksheet, binds the writes, and renders CompensationView. */
export function CompensationTab({ requisitionId, applicationId, reload }: OfferTabProps) {
  const { me } = useSession();
  const [failure, setFailure] = useState<string | undefined>(undefined);
  const key = ["comp.open_worksheet", requisitionId, applicationId] as const;
  const read = useRead(key, () => compOpenWorksheet({ requisition_id: requisitionId, application_id: applicationId }) as unknown as Promise<CompOut>);
  // a write settles only once the worksheet is read back, so the next write names the version it now shows
  const done = async () => { setFailure(undefined); await Promise.all([read.refetch(), reload?.()]); };
  const fail = (e: unknown) => { setFailure(errorText(e)); };

  const prepare = useWrite((values: PrepareValues, idem: string) => {
    const comp = read.data;
    const currency = comp?.package?.currency ?? comp?.recommendation?.range?.currency ?? undefined;
    return compPropose({
      requisition_id: requisitionId, application_id: applicationId, base: values.base.trim(),
      bonus_target: blank(values.bonus_target), sign_on: blank(values.sign_on), start_date: values.start_date,
      commission: blank(values.commission), relocation: blank(values.relocation), lti: blank(values.lti),
      lti_wording: blank(values.lti_wording), ...(currency ? { currency } : {}),
    }, idem);
  }, [key]);
  const version = () => read.data?.package?.version ?? 0;
  // comp.record_package is the Draft -> Pending Approvals send (B8 plan D15).
  const send = useWrite((_: null, idem: string) => compRecordPackage({ requisition_id: requisitionId, application_id: applicationId, package_version: version() }, idem), [key]);
  const decide = useWrite((input: DecideInput, idem: string) => {
    const base = { requisition_id: requisitionId, application_id: applicationId, package_version: version(), note: input.note ?? null };
    if (input.key === "org_leader") return compRecordLeaderApproval({ ...base, approver_name: input.approver_name ?? "", evidence_file_id: input.evidence?.file_id ?? null, evidence_ref: input.evidence?.ref ?? null }, idem);
    if (input.key === "total_rewards") return compApproveTotalRewards({ ...base, approved_amount: input.approved_amount ?? "", approved_wording: input.approved_wording ?? "" }, idem);
    return compApprove({ ...base, decision: input.decision === "approved" ? "approve" : input.decision }, idem);
  }, [key]);

  if (read.isLoading) return <Card title={PKG_LABELS.offerPackage} loading={{ lines: 6 }}><Skeleton lines={6} /></Card>;
  if (read.error) {
    return isClientRefusal(read.error)
      ? <Card title={PKG_LABELS.offerPackage} refused={{ reason: errorText(read.error) }}>{null}</Card>
      : <ErrorStrip message={errorText(read.error)} onRetry={() => { void read.refetch(); }} retrying={read.isFetching} />;
  }
  const comp = read.data;
  if (!comp) return null;
  const actions: CompActions = {
    prepare: (values) => prepare.mutateAsync(values).then(done, fail),
    send: () => send.mutateAsync(null).then(done, fail),
    decide: (input) => decide.mutateAsync(input).then(done, fail),
    upload: async (file) => {
      const stored = await uploadFile(file, { owner_type: "offer", owner_id: applicationId, kind: "comp_evidence", requisition_id: requisitionId } as unknown as Parameters<typeof uploadFile>[1]);
      return stored.file_id;
    },
    extend: () => {
      // the Offer tab records the verbal offer (offer.record_verbal needs the terms conveyed)
      const hash = window.location.hash;
      window.location.hash = /[?&]tab=/.test(hash) ? hash.replace(/([?&])tab=[^&]*/, "$1tab=offer") : `${hash}${hash.includes("?") ? "&" : "?"}tab=offer`;
    },
  };
  return (
    <CompensationView
      comp={comp}
      roles={me?.roles ?? []}
      actions={actions}
      timeZone={me?.timezone}
      error={failure}
    />
  );
}
